import { createContext, forwardRef, useCallback, useContext, useEffect, type ReactNode } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useLocation,
  useNavigate,
  type LinkProps,
  type NavigateOptions,
  type NavLinkProps,
  type To,
} from 'react-router';
import {
  CONDITION_LABELS,
  decadeLabel,
  formatMoney,
  LOCALE_TAGS,
  localizePath,
  splitLocalePath,
  type Condition,
  type Locale,
} from '@shop/shared';
import { en, lt, type Dictionary } from './dictionary';
import { setRequestLocale } from './locale-state';

const DICTIONARIES: Record<Locale, Dictionary> = { en, lt };
const LocaleContext = createContext<Locale>('en');

/**
 * The language comes from the URL: /lt/... is Lithuanian, everything else English.
 * That keeps every page shareable and indexable in both languages.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const locale = splitLocalePath(pathname).locale;
  // Set before children render so their API requests carry the right ?lang=.
  setRequestLocale(locale);
  useEffect(() => {
    document.documentElement.lang = LOCALE_TAGS[locale];
  }, [locale]);
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => useContext(LocaleContext);
export const useT = () => DICTIONARIES[useLocale()];

/** Locale-aware formatting helpers. */
export function useFormat() {
  const locale = useLocale();
  const tag = LOCALE_TAGS[locale];
  return {
    decade: (d: number) => decadeLabel(d, locale),
    condition: (c: Condition) => CONDITION_LABELS[locale][c],
    money: (minor: number, currency: string) => formatMoney(minor, currency, tag),
    country: (code: string) => {
      try {
        return new Intl.DisplayNames([tag], { type: 'region' }).of(code) ?? code;
      } catch {
        return code;
      }
    },
  };
}

function localizeTo(to: To, locale: Locale): To {
  if (typeof to === 'string') return localizePath(to, locale);
  return to.pathname ? { ...to, pathname: localizePath(to.pathname, locale) } : to;
}

/** react-router's Link, with "/shop" turned into "/lt/shop" on Lithuanian pages. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...rest }, ref) {
  const locale = useLocale();
  return <RouterLink ref={ref} to={localizeTo(to, locale)} {...rest} />;
});

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink({ to, ...rest }, ref) {
  const locale = useLocale();
  return <RouterNavLink ref={ref} to={localizeTo(to, locale)} {...rest} />;
});

export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const locale = useLocale();
  return useCallback((to: To, options?: NavigateOptions) => navigate(localizeTo(to, locale), options), [navigate, locale]);
}

/** The current page in another language (same path and query). */
export function useAlternateHref() {
  const { pathname, search, hash } = useLocation();
  const { path } = splitLocalePath(pathname);
  return (target: Locale) => `${localizePath(path, target)}${search}${hash}`;
}
